import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';

export const MOTIVO_DEFEITO_TOOLTIP = 'Motivo padrão que será preenchido na nova ordem de serviço.';
export const TOOLTIP_ATUALIZA_SLA =
  'Em requisição de peça vinculada, quando está liberada no status configurado, atualiza o SLA da ordem de serviço.';

export const TOOLTIP_INSTALA_EQUIP =
  'Quando habilitado, atualiza a data de instalação do equipamento para data do atendimento feito, na ordem de serviço de instalação';

export const TOOLTIP_DESINSTALA_EQUIP =
  'Quando habilitado, atualiza a data de desinstalação do equipamento para data do atendimento feito, na ordem de serviço de desinstalação';

export const TOOLTIP_NAO_LIGA =
  'Ao marcar, será a condição fixa que o técnico informa no atendimento que o equipamento não liga. Só pode habilitar esta função em uma condição, apenas.';

export const TOOLTIP_SEM_PENDENCIA =
  'Exclusivo de uso do App Go > Ao marcar, será a condição padrão para quando técnico informar no atendimento não possui pendências. No caso a OS assumirá esta condição automaticamente. Exige Tipos de OS: Corretiva, Preventiva, Estoque, Recarga e Aferição; não pode incluir Instalação nem Desinstalação.';

export const TOOLTIP_TIPO_OS =
  'Definir quais tipos de OS esta condição poderá ser selecionada.';

/** Flags Tipo de OS em LCMY_REG_CONDITION (0/1). */
export const COND_TIPO_OS_FIELDS = [
  { db: 'allow_corrective', label: 'Corretiva' },
  { db: 'allow_install', label: 'Instalação' },
  { db: 'allow_desinstall', label: 'Desinstalação' },
  { db: 'allow_preventive', label: 'Preventiva' },
  { db: 'allow_stock', label: 'Estoque' },
  { db: 'allow_recharge', label: 'Recarga' },
  { db: 'allow_measurement', label: 'Aferição' },
  { db: 'allow_counter', label: 'Balcão' },
];

/** Tipos obrigatórios para habilitar "Sem pendência". */
export const SEM_PENDENCIA_TIPO_OS_REQUIRED = [
  { db: 'allow_corrective', label: 'Corretiva' },
  { db: 'allow_preventive', label: 'Preventiva' },
  { db: 'allow_stock', label: 'Estoque' },
  { db: 'allow_recharge', label: 'Recarga' },
  { db: 'allow_measurement', label: 'Aferição' },
];

/** Tipos proibidos quando "Sem pendência" está ativo. */
export const SEM_PENDENCIA_TIPO_OS_FORBIDDEN = [
  { db: 'allow_install', label: 'Instalação' },
  { db: 'allow_desinstall', label: 'Desinstalação' },
];

export function normCondOsFlag(v) {
  return v === 1 || v === true || v === '1' ? 1 : 0;
}

export function getSemPendenciaTipoOsIssues(row) {
  const missing = SEM_PENDENCIA_TIPO_OS_REQUIRED.filter(
    (f) => normCondOsFlag(row?.[f.db]) !== 1
  ).map((f) => f.label);
  const forbiddenOn = SEM_PENDENCIA_TIPO_OS_FORBIDDEN.filter(
    (f) => normCondOsFlag(row?.[f.db]) === 1
  ).map((f) => f.label);
  return { missing, forbiddenOn };
}

export function formatSemPendenciaTipoOsBlockMessage(issues, { forEnable = true } = {}) {
  const missing = issues?.missing || [];
  const forbiddenOn = issues?.forbiddenOn || [];
  if (!missing.length && !forbiddenOn.length) return null;
  const parts = [];
  if (missing.length) {
    parts.push(
      forEnable
        ? `é obrigatório marcar: ${missing.join(', ')}`
        : `mantenha marcados: ${missing.join(', ')}`
    );
  }
  if (forbiddenOn.length) {
    parts.push(
      forEnable
        ? `não pode estar marcado: ${forbiddenOn.join(', ')}`
        : `desmarque: ${forbiddenOn.join(', ')}`
    );
  }
  const prefix = forEnable
    ? 'Não é possível ativar "Sem pendência"'
    : 'Com "Sem pendência" ativo, esta alteração nos Tipos de OS não é permitida';
  return `${prefix}: ${parts.join('; ')}.`;
}

export function rowHasSemPendencia(row) {
  return row?.func_sem_pendencia === 1 || row?.func_sem_pendencia === true || String(row?.func_sem_pendencia) === '1';
}

export const CAMPOS_CONFIG = [
  { key: 'operacao', label: 'Operação', maxLen: 2 },
  { key: 'condicao', label: 'Condição', maxLen: 3 },
  { key: 'status_disponivel', label: 'Status disponível', maxLen: 2 },
  { key: 'status_indisponivel', label: 'Status indisponível', maxLen: 2 },
  { key: 'status_retorno', label: 'Status de retorno', maxLen: 2 },
  {
    key: 'status_peca_disp',
    label: 'Status peça Disp.',
    maxLen: 2,
    tooltip:
      'Status do pedido de peça no momento em que estiver liberado (processo de saída de estoque).',
    tooltipIcon: true,
  },
];

export const CAMPOS_GRUPO_KEYS = ['operacao', 'condicao', 'status_disponivel', 'status_indisponivel'];

/** Mantém só A-Z/0-9 (maiúsculo), limitado a `len`. */
export function sanitizeAlnumCode(value, len) {
  return String(value ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, len);
}

export function isExactAlnumCode(value, len) {
  return new RegExp(`^[A-Z0-9]{${len}}$`).test(String(value ?? '').trim().toUpperCase());
}

export const CAMPOS_INTERV_PREFIX = CAMPOS_CONFIG.slice(0, 5);
export const CAMPOS_INTERV_SUFFIX = CAMPOS_CONFIG.slice(5);

export const COND_INTERV_COL_TOGGLE_KEYS = [
  'tipo_os',
  'codigo',
  'atualiza_sla',
  'operacao',
  'condicao',
  'status_disponivel',
  'status_indisponivel',
  'status_retorno',
  'defeito',
  'status_peca_disp',
  'func_install',
  'func_uninstall',
  'func_counters',
  'func_nao_liga',
  'func_sem_pendencia',
];

export const COND_INTERV_COL_LABELS = {
  codigo: 'Código',
  atualiza_sla: 'Atualiza SLA',
  operacao: 'Operação',
  condicao: 'Condição',
  status_disponivel: 'Status disponível',
  status_indisponivel: 'Status indisponível',
  status_retorno: 'Status de retorno',
  defeito: 'Defeito',
  status_peca_disp: 'Status peça Disp.',
  func_install: 'Instala equipamento',
  func_uninstall: 'Desinstala equipamento',
  func_counters: 'Notifica contadores',
  func_nao_liga: 'Não liga',
  func_sem_pendencia: 'Sem pendência',
  tipo_os: 'Tipo de OS',
};

export const CI2_GROUPS = [
  {
    key: 'pedido',
    label: 'Gera retorno e pedido',
    desc: 'Status de retorno + Operação preenchidos',
    color: 'var(--ci2-violet, #6E56CF)',
  },
  {
    key: 'retorno',
    label: 'Gera retorno',
    desc: 'Status de retorno preenchido',
    color: 'var(--ci2-blue, #265ED4)',
  },
  {
    key: 'nada',
    label: 'Não gera nada',
    desc: 'Sem retorno nem pedido associados',
    color: 'var(--ci2-teal, #0E8A82)',
  },
  {
    key: 'desabilitada',
    label: 'Desabilitadas',
    desc: 'Ainda não configuradas',
    color: 'var(--ci2-gray, #767889)',
  },
];

export const emptyGrupoValues = () => ({
  operacao: '',
  condicao: '',
  status_disponivel: '',
  status_indisponivel: '',
  stock_validation_scope: 'all_companies',
});

export const STOCK_VALIDATION_SCOPE = {
  ALL_COMPANIES: 'all_companies',
  OS_COMPANY: 'os_company',
};

export const STOCK_VALIDATION_OPTIONS = [
  {
    value: STOCK_VALIDATION_SCOPE.ALL_COMPANIES,
    label: 'Todas empresas',
    tip: 'Valida estoque de todas empresas para gerar pedido único.',
  },
  {
    value: STOCK_VALIDATION_SCOPE.OS_COMPANY,
    label: 'Por empresa',
    tip: 'Valida estoque da empresa da OS para gerar pedido único.',
  },
];

export function normalizeStockValidationScope(raw) {
  const s = String(raw ?? '').trim().toLowerCase();
  if (s === STOCK_VALIDATION_SCOPE.OS_COMPANY) return STOCK_VALIDATION_SCOPE.OS_COMPANY;
  return STOCK_VALIDATION_SCOPE.ALL_COMPANIES;
}

export function sanitizeMotivoDraft(raw) {
  const s = String(raw ?? '');
  return s
    .replace(/'/g, '')
    .replace(/\//g, '')
    .replace(/[^A-Za-zÀ-ÖØ-öø-ÿ0-9 ]/g, '')
    .slice(0, 50);
}

export function rowTemConfigDefeito(row) {
  const cod = row.codbug != null && String(row.codbug).trim() !== '';
  const reason = row.codbug_reason != null && String(row.codbug_reason).trim() !== '';
  const blank = row.codbug_blank === 1 || row.codbug_blank === true;
  return cod || reason || blank;
}

export function rowDefeitoHoverTooltip(row, native = false) {
  const lines = [];
  if (row.codbug != null && String(row.codbug).trim() !== '') {
    const nome = String(row.codbug_nome || row.codbug).trim();
    const al = row.codbug_alert != null && String(row.codbug_alert).trim() !== '' ? String(row.codbug_alert) : '—';
    lines.push(`${nome} · ${al}`);
  }
  if (row.codbug_reason != null && String(row.codbug_reason).trim() !== '') {
    lines.push(`Motivo: ${String(row.codbug_reason).trim()}`);
  }
  if (row.codbug_blank === 1 || row.codbug_blank === true) {
    lines.push(native ? 'Abrir sem serviço' : 'Abrir sem defeito');
  }
  return lines.join('\n');
}

export function labelRegBugOption(b) {
  return `${String(b.TB01048_NOME || b.cod_bug || '').trim()} · ${b.cod_alert != null ? String(b.cod_alert) : '—'}`;
}

export function categorizeCondicaoRow(row) {
  if (row.func_close !== 1) return 'desabilitada';
  const hasRetorno = String(row.status_retorno ?? '').trim() !== '';
  const hasOperacao = String(row.operacao ?? '').trim() !== '';
  if (hasRetorno && hasOperacao) return 'pedido';
  if (hasRetorno) return 'retorno';
  return 'nada';
}

export function DefeitoRegBugDropdown({
  value,
  regBugsList,
  disabled,
  rowCod,
  openCod,
  setOpenCod,
  onChange,
  buttonId,
  variant = 'legacy',
}) {
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const [listLayout, setListLayout] = useState(null);
  const rowKey = String(rowCod);
  const open = openCod != null && String(openCod) === rowKey;

  const updateListLayout = () => {
    if (!open || !triggerRef.current) {
      setListLayout(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setListLayout({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    const fn = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      setOpenCod(null);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open, setOpenCod]);

  useLayoutEffect(() => {
    if (variant !== 'ci2') return;
    updateListLayout();
  }, [open, variant, regBugsList]);

  useEffect(() => {
    if (variant !== 'ci2' || !open) return undefined;
    const onReposition = () => updateListLayout();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, variant, regBugsList]);

  const label = (() => {
    const v = value != null ? String(value).trim() : '';
    if (v === '') return '(Nenhum)';
    const b = regBugsList.find((x) => String(x.cod_bug || '').trim() === v);
    return b ? labelRegBugOption(b) : v;
  })();

  const sel = value != null ? String(value).trim() : '';

  const pickOption = (cod) => {
    onChange(cod);
    setOpenCod(null);
  };

  if (variant === 'ci2') {
    const listContent = (
      <>
        <button
          type="button"
          role="option"
          aria-selected={sel === ''}
          className={`ci2-defeito-picker-item${sel === '' ? ' is-selected' : ''}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => pickOption('')}
        >
          <span className="ci2-defeito-picker-item-label">(Nenhum)</span>
          <Check size={13} strokeWidth={2.25} className="ci2-defeito-picker-check" aria-hidden />
        </button>
        {regBugsList.map((b) => {
          const cod = String(b.cod_bug || '').trim();
          const selected = cod === sel;
          return (
            <button
              key={cod || `bug-${String(b.TB01048_NOME)}`}
              type="button"
              role="option"
              aria-selected={selected}
              className={`ci2-defeito-picker-item${selected ? ' is-selected' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pickOption(cod)}
            >
              <span className="ci2-defeito-picker-item-label" title={labelRegBugOption(b)}>
                {labelRegBugOption(b)}
              </span>
              <Check size={13} strokeWidth={2.25} className="ci2-defeito-picker-check" aria-hidden />
            </button>
          );
        })}
      </>
    );

    return (
      <div
        className={`ci2-defeito-picker${open ? ' ci2-defeito-picker--open' : ''}`}
        ref={wrapRef}
      >
        <button
          ref={triggerRef}
          type="button"
          className="ci2-defeito-picker-trigger"
          id={buttonId}
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label="Selecionar defeito"
          onClick={() => setOpenCod(open ? null : rowKey)}
        >
          <span className="ci2-defeito-picker-trigger-label">{label}</span>
          <ChevronDown
            size={12}
            strokeWidth={2}
            className={`ci2-defeito-picker-chevron${open ? ' is-open' : ''}`}
            aria-hidden
          />
        </button>
        {open && listLayout
          ? ReactDOM.createPortal(
              <div
                ref={listRef}
                className="ci2-defeito-picker-list ci2-defeito-picker-list--portal"
                role="listbox"
                style={{
                  position: 'fixed',
                  top: listLayout.top,
                  left: listLayout.left,
                  width: listLayout.width,
                  zIndex: 12050,
                }}
              >
                {listContent}
              </div>,
              document.body
            )
          : null}
      </div>
    );
  }

  return (
    <div
      className={`defeitos-vv-sev-select condicoes-defeito-reg-dd${open ? ' defeitos-vv-sev-select--open' : ''}`}
      ref={wrapRef}
    >
      <button
        type="button"
        className="defeitos-vv-sev-trigger"
        id={buttonId}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Selecionar defeito"
        onClick={() => setOpenCod(open ? null : rowKey)}
      >
        <span className="defeitos-vv-sev-trigger-label">{label}</span>
        <ChevronDown
          size={9.9}
          strokeWidth={1.8}
          className={`defeitos-vv-sev-chevron${open ? ' defeitos-vv-sev-chevron--open' : ''}`}
          aria-hidden
        />
      </button>
      {open ? (
        <div className="defeitos-vv-sev-dropdown" role="listbox">
          <div
            role="option"
            aria-selected={sel === ''}
            className="defeitos-vv-sev-option"
            data-selected={sel === ''}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onChange('');
              setOpenCod(null);
            }}
          >
            (Nenhum)
          </div>
          {regBugsList.map((b) => {
            const cod = String(b.cod_bug || '').trim();
            return (
              <div
                key={cod || `bug-${String(b.TB01048_NOME)}`}
                role="option"
                aria-selected={cod === sel}
                className="defeitos-vv-sev-option"
                data-selected={cod === sel}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(cod);
                  setOpenCod(null);
                }}
              >
                {labelRegBugOption(b)}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
